import type { ReactNode } from 'react';

// renderInlineRich — co-extracted verbatim from the monolith (legacy/App.tsx); the
// only caller is RichText below, so it lives here as a module-local helper.
// Renders inline **bold** / *italic* markdown within a single text run.
function renderInlineRich(s: any, keyBase: any): ReactNode[] {
  const parts: ReactNode[] = [];
  let rest = String(s);
  let k = 0;
  const re = /(\*\*([^*]+)\*\*|\*([^*]+)\*)/;
  let m;
  while ((m = re.exec(rest))) {
    if (m.index > 0) parts.push(rest.slice(0, m.index));
    if (m[2] != null) parts.push(<strong key={`${keyBase}-${k++}`}>{m[2]}</strong>);
    else parts.push(<em key={`${keyBase}-${k++}`}>{m[3]}</em>);
    rest = rest.slice(m.index + m[0].length);
  }
  if (rest) parts.push(rest);
  return parts;
}

export interface RichTextProps {
  text?: string;
  className?: string;
}

/**
 * Lightweight rich-text renderer — splits on newlines into paragraphs and
 * bullet lists (dash, asterisk or bullet prefixes), with inline bold/italic
 * markdown. Extracted verbatim from the monolith (legacy/App.tsx); markup unchanged.
 */
export function RichText({ text, className = '' }: RichTextProps) {
  if (!text || !String(text).trim()) return null;
  const lines = String(text).split('\n');
  const blocks: any[] = [];
  let bullets: any = null;
  const flush = () => {
    if (bullets) {
      blocks.push({ type: 'ul', items: bullets });
      bullets = null;
    }
  };
  lines.forEach((ln) => {
    const t = ln.trim();
    if (t.startsWith('- ') || t.startsWith('* ') || t.startsWith('• ')) {
      if (!bullets) bullets = [];
      bullets.push(t.slice(2));
    } else {
      flush();
      blocks.push({ type: 'p', text: ln });
    }
  });
  flush();
  return (
    <div className={`space-y-1.5 text-sm text-ink-2 ${className}`}>
      {blocks.map((b, i) =>
        b.type === 'ul' ? (
          <ul key={i} className="list-disc pl-5 space-y-0.5">
            {b.items.map((it: any, j: number) => (
              <li key={j}>{renderInlineRich(it, `${i}-${j}`)}</li>
            ))}
          </ul>
        ) : b.text.trim() ? (
          <p key={i}>{renderInlineRich(b.text, i)}</p>
        ) : null,
      )}
    </div>
  );
}
